import { useEffect, useRef, useState } from "react";
import { isAppMode } from "@/app-bridge/core/wrapper";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  Check,
  Circle,
  Film,
  Images,
  Loader2,
  ScanSearch,
  ShieldCheck,
  Sparkles,
  Upload,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { getScanQuota } from "@/lib/video-scan.functions";
import { myRoleQuery } from "@/lib/queries";
import { SCAN_MAX_SECONDS, type ScanItem } from "@/lib/video-scan";
import { formatBytes } from "@/lib/video-compress";
import { useScanJob } from "@/components/scan/ScanJobProvider";
import { useVideoPoster } from "@/lib/use-video-poster";
import { cn } from "@/lib/utils";

/** "1 min 12 s" – används i debug och i resultatet. */
function formatDuration(seconds: number): string {
  const s = Math.round(seconds);
  if (!Number.isFinite(s) || s <= 0) return "okänd längd";
  return s >= 60 ? `${Math.floor(s / 60)} min ${s % 60} s` : `${s} sekunder`;
}

const kr = (n: number) => `${n.toLocaleString("sv-SE")} kr`;

const CONFIDENCE: Record<ScanItem["confidence"], { label: string; className: string }> = {
  high: { label: "Säker", className: "bg-primary/10 text-primary" },
  medium: { label: "Trolig", className: "bg-muted text-muted-foreground" },
  low: {
    label: "Osäker",
    className: "bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-300",
  },
};

export type VideoScannerProps = {
  /** Öppnar filväljaren direkt när skannern visas (t.ex. valt läge på startsidan). */
  autoStart?: boolean;
  /** Anropas när körningen sparats, så listan kan uppdateras och märka den som ny. */
  onSaved?: (runId: string, label: string) => void;
};

export function VideoScanner({ onSaved, autoStart = false }: VideoScannerProps) {
  const job = useScanJob();
  const inputRef = useRef<HTMLInputElement>(null);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [pendingPreview, setPendingPreview] = useState<string | null>(null);

  const fetchQuota = useServerFn(getScanQuota);
  const { data: quota, refetch: refetchQuota } = useQuery({
    queryKey: ["scan-quota"],
    queryFn: () => fetchQuota({}),
  });
  // Teknisk logg visas bara för admin/superadmin.
  const { data: roleData } = useQuery(myRoleQuery());
  const isAdminViewer = roleData?.role === "admin" || roleData?.role === "superadmin";


  const isVideoJob = job.kind === "video";
  const {
    step: rawStep,
    error,
    fileName,
    items,
    overall,
    statusText,
    debug,
    savedRunId,
    savedRunLabel,
    savedAt,
    busy: rawBusy,
    sourcePreviewUrl,
    framePreviewUrls,
    frameCount,
  } = job;
  // Fotoskanningen delar samma jobb-state; här visar vi bara filmkörningar.
  const step = isVideoJob ? rawStep : "idle";
  const busy = isVideoJob && rawBusy;

  const reportedRef = useRef(0);
  // Startas skannern från ett val någon annanstans öppnar vi videovalet direkt.
  const autoStartedRef = useRef(false);
  useEffect(() => {
    if (!autoStart || autoStartedRef.current || busy || pendingFile) return;
    autoStartedRef.current = true;
    const id = window.setTimeout(() => openPicker(), 250);
    return () => window.clearTimeout(id);
  }, [autoStart, busy, pendingFile]);
  // Första bildrutan visas som stillbild i stället för en svart ruta.
  const pendingPoster = useVideoPoster(pendingPreview);
  const sourcePoster = useVideoPoster(isVideoJob ? sourcePreviewUrl : null);

  useEffect(() => () => {
    if (pendingPreview) URL.revokeObjectURL(pendingPreview);
  }, [pendingPreview]);

  function chooseFile(file: File) {
    if (pendingPreview) URL.revokeObjectURL(pendingPreview);
    setPendingFile(file);
    setPendingPreview(URL.createObjectURL(file));
  }

  /** I appen använder vi telefonens egen videokamera; i webben den vanliga filväljaren. */
  function openPicker() {
    if (!isAppMode()) {
      inputRef.current?.click();
      return;
    }
    void import("@/app-bridge/core/wrapper").then(async (m) => {
      const clip = await m.recordNativeVideo();
      if (clip) {
        chooseFile(clip);
        return;
      }
      inputRef.current?.click();
    });
  }

  function beginPendingScan() {
    if (!pendingFile) return;
    job.start(pendingFile);
    setPendingFile(null);
    if (pendingPreview) URL.revokeObjectURL(pendingPreview);
    setPendingPreview(null);
  }

  // När bakgrundsjobbet sparat en körning uppdaterar sidan sin lista.
  useEffect(() => {
    if (!savedAt || savedAt === reportedRef.current) return;
    reportedRef.current = savedAt;
    void refetchQuota();
    if (savedRunId && savedRunLabel) onSaved?.(savedRunId, savedRunLabel);
  }, [savedAt, savedRunId, savedRunLabel, onSaved, refetchQuota]);

  const total = items?.reduce(
    (acc, i) => ({ low: acc.low + i.valueLow, high: acc.high + i.valueHigh }),
    { low: 0, high: 0 },
  );

  // Användaren ska bara se enkla faser – tekniken ligger i adminloggen.
  const userStages = ["Laddar upp film", "Granskar", "Sparar dina prylar"];
  /** Fas 0 = filmen laddas upp, 1 = AI:n granskar, 2 = prylarna sparas. */
  const phase = step === "save" ? 2 : step === "scan" || job.handoff ? 1 : 0;
  /** Sant bara medan filmen faktiskt laddas upp. */
  const uploading = phase === 0 && !job.handoff;
  const visibleProgress = job.handoff ? null : Math.round(overall * 100);



  return (
    <div>
      <div className="overflow-hidden rounded-xl border border-border bg-card">
        <input
          ref={inputRef}
          type="file"
          accept="video/*"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            e.target.value = "";
            if (file) chooseFile(file);
          }}
        />
        {step === "idle" && pendingFile && pendingPreview ? (
          <div className="grid gap-4 p-4 sm:grid-cols-[minmax(0,240px)_minmax(0,1fr)] sm:p-7">
            <div className="min-w-0 overflow-hidden rounded-xl border border-border bg-muted">
              <video src={pendingPreview} poster={pendingPoster ?? undefined} controls muted playsInline preload="metadata" className="max-h-56 w-full object-contain" />
            </div>

            <div className="min-w-0">

              <p className="text-xs font-semibold uppercase text-primary">Redo att skanna</p>
              <h3 className="mt-2 break-all font-heading text-lg font-bold sm:truncate sm:break-normal sm:text-xl">{pendingFile.name}</h3>
              <p className="mt-1 text-sm text-muted-foreground">{formatBytes(pendingFile.size)} · kontrollera att filmen visar rummet du vill skanna.</p>
              <div className="mt-5 flex flex-col gap-2 sm:flex-row">
                <Button className="w-full gap-2 sm:w-auto" onClick={beginPendingScan}>
                  <ScanSearch className="h-4 w-4" />
                  Starta videoskanning
                </Button>
                <Button variant="outline" className="w-full sm:w-auto" onClick={openPicker}>Välj en annan film</Button>
              </div>
              <p className="mt-4 text-xs leading-relaxed text-muted-foreground">Vi läser originalfilmen scen för scen och sparar bara de skarpaste bilderna. Du ser varje bild växa fram – lyckas det inte startas ingen skanning.</p>
            </div>

          </div>
        ) : step === "idle" || step === "done" ? (
          <div className="grid sm:grid-cols-[minmax(0,1.1fr)_minmax(220px,.9fr)]">
            <div className="p-5 sm:p-7">
              <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary/10 text-primary">
                {step === "done" ? <Check className="h-6 w-6" /> : <Film className="h-6 w-6" />}
              </span>
              <h3 className="mt-4 font-heading text-xl font-bold">
                {step === "done" ? "Videoskanningen är klar" : "Filma ett rum – få en lista med bilder"}
              </h3>
              <p className="mt-2 max-w-lg text-sm leading-relaxed text-muted-foreground">
                {step === "done"
                  ? `${fileName ?? "Filmen"} har gåtts igenom och varje hittad pryl har fått en bild ur filmen.`
                  : `Välj en film på högst ${SCAN_MAX_SECONDS} sekunder. Vi hittar prylarna, uppskattar värdet och sparar en riktig bildruta på varje sak.`}
              </p>
              {quota && (
                <p className="mt-3 text-xs font-medium text-muted-foreground">
                  {quota.unlimited
                    ? "Obegränsat antal skanningar för administratör"
                    : `${Math.max(0, quota.perHour - quota.lastHour)} kvar den här timmen · ${Math.max(0, quota.perDay - quota.lastDay)} kvar i dag`}
                </p>
              )}
              <Button
                size="lg"
                className="mt-5 w-full gap-2 sm:w-auto"
                onClick={openPicker}
              >
                <Upload className="h-5 w-5" />
                {step === "done" ? "Skanna en ny film" : "Välj eller spela in film"}
              </Button>
              <p className="mt-3 text-xs leading-relaxed text-muted-foreground">
                Så startar du: tryck på knappen, spela in eller välj en film på högst{" "}
                {SCAN_MAX_SECONDS} sekunder och tryck sedan på Starta videoskanning.
              </p>
            </div>
            <div className="border-t border-border bg-muted/30 p-5 sm:border-l sm:border-t-0 sm:p-6">
              <p className="text-xs font-semibold uppercase text-muted-foreground">För bästa resultat</p>
              <ul className="mt-4 space-y-3 text-sm">
                <li className="flex gap-2.5"><Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-primary" />Gå långsamt och håll varje pryl i bild.</li>
                <li className="flex gap-2.5"><Images className="mt-0.5 h-4 w-4 shrink-0 text-primary" />Ha bra ljus och undvik snabba panoreringar.</li>
                <li className="flex gap-2.5"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-primary" />Filmen raderas när bilderna har sparats.</li>
              </ul>
              {step === "done" && debug && (
                <p className="mt-4 border-t border-border pt-4 text-xs text-muted-foreground">
                  {formatDuration(debug.durationSeconds)} · {formatBytes(debug.originalBytes)} · {frameCount} bildrutor
                </p>
              )}
            </div>
          </div>
        ) : (
          <div className="grid sm:grid-cols-[minmax(0,.85fr)_minmax(0,1.15fr)]">
            <div className="min-w-0 border-b border-border bg-foreground/5 p-4 sm:border-b-0 sm:border-r">
              <div className="flex items-center justify-center overflow-hidden rounded-lg bg-black/80">
                {sourcePreviewUrl ? (
                  <video
                    src={sourcePreviewUrl}
                    poster={sourcePoster ?? undefined}
                    muted
                    playsInline
                    preload="metadata"
                    className="max-h-64 w-full object-contain"
                  />
                ) : (
                  <div className="flex h-40 w-full items-center justify-center">
                    <Film className="h-10 w-10 text-muted-foreground" />
                  </div>
                )}
              </div>
              <p className="mt-3 truncate text-sm font-medium">{fileName}</p>
              {debug && (
                <p className="text-xs text-muted-foreground">
                  {formatDuration(debug.durationSeconds)} · {formatBytes(debug.originalBytes)}
                </p>
              )}
            </div>
            <div className="min-w-0 p-4 sm:p-6">
              <div className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-3">
                <div className="min-w-0">
                  <p className="text-xs font-semibold uppercase text-primary">Videoskanning</p>
                  <h3 className="mt-1 break-words font-heading text-lg font-bold sm:text-xl">
                    {uploading
                      ? "Vi laddar upp din film…"
                      : phase === 2
                        ? "Vi sparar dina prylar…"
                        : "Vi granskar filmen…"}
                  </h3>
                </div>
                {visibleProgress == null ? (
                  <span className="shrink-0 text-xs font-semibold text-primary">Körs i bakgrunden</span>
                ) : (
                  <span className="shrink-0 font-heading text-2xl font-bold tabular-nums">{visibleProgress}%</span>
                )}
              </div>
              <div className={cn(
                "mt-4 rounded-lg border px-3 py-3 text-sm",
                uploading
                  ? "border-amber-300 bg-amber-50 text-amber-950 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-100"
                  : "border-primary/30 bg-primary/5",
              )}>
                <div className="flex items-start gap-2.5">
                  {uploading ? (
                    <Upload className="mt-0.5 h-4 w-4 shrink-0" />
                  ) : (
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                  )}
                  <div className="min-w-0">
                    <p className="font-semibold">
                      {uploading ? "Håll sidan öppen en liten stund" : "Klart – du kan stänga sidan nu"}
                    </p>
                    <p className="mt-0.5 text-xs leading-relaxed opacity-80">
                      {uploading
                        ? "Vi laddar upp filmen. Så fort den är uppe säger vi till – då kan du stänga sidan."
                        : "Filmen är uppladdad och skanningen fortsätter i bakgrunden. Du får en notis i inkorgen när den är klar."}
                    </p>
                  </div>
                </div>
              </div>
              {visibleProgress != null && (
                <div className="mt-4 h-2 w-full overflow-hidden rounded-full bg-muted">
                  <div
                    className="h-full rounded-full bg-primary transition-all duration-300"
                    style={{ width: `${Math.max(3, visibleProgress)}%` }}
                  />
                </div>
              )}
              <ol className="mt-5 space-y-2.5">
                {userStages.map((label, index) => {
                  const complete = phase > index;
                  const active = phase === index;
                  return <li key={label} className={cn("flex items-center gap-2.5 text-sm", !complete && !active && "text-muted-foreground")}>
                    {complete ? <Check className="h-4 w-4 shrink-0 text-primary" /> : active ? <Loader2 className="h-4 w-4 shrink-0 animate-spin text-primary" /> : <Circle className="h-4 w-4 shrink-0" />}
                    <span className={cn("min-w-0", active && "font-medium text-foreground")}>{label}</span>
                  </li>;
                })}
              </ol>


              {isAdminViewer && (
                <details className="mt-5 rounded-lg border border-border bg-muted/40 px-3 py-2 text-xs">
                  <summary className="cursor-pointer font-semibold">Teknisk logg (admin)</summary>
                  <div className="mt-3 space-y-1.5 break-words text-muted-foreground">
                    <p>Steg: <span className="text-foreground">{step}</span> · {Math.round(overall * 100)}% · {statusText}</p>
                    <p>Bildrutor: <span className="text-foreground">{frameCount}</span> skarpa{job.sceneCount ? ` av ${job.sceneCount} scener` : ""}</p>
                    {debug && (
                      <>
                        <p>Fil: <span className="text-foreground">{debug.name}</span> · {debug.originalType}</p>
                        <p>
                          {formatDuration(debug.durationSeconds)} · original {formatBytes(debug.originalBytes)}
                          {debug.totalCompressedBytes ? ` · komprimerad ${formatBytes(debug.totalCompressedBytes)}` : ""}
                          {debug.compressMs ? ` · komprimering ${Math.round(debug.compressMs / 100) / 10}s` : ""}
                        </p>
                      </>
                    )}
                    {job.jobId && <p className="break-all">Jobb-id: <span className="font-mono text-foreground">{job.jobId}</span></p>}
                    {savedRunId && <p className="break-all">Körnings-id: <span className="font-mono text-foreground">{savedRunId}</span></p>}
                    {job.failureDiagnostic && (
                      <div className="mt-2 rounded-md border border-destructive/30 bg-destructive/5 p-2 text-destructive">
                        <p className="font-semibold">{job.failureDiagnostic.code} · {job.failureDiagnostic.stage}</p>
                        <p>{job.failureDiagnostic.name}: {job.failureDiagnostic.message}</p>
                        <p className="mt-1 break-all font-mono text-[10px] opacity-80">{job.failureDiagnostic.userAgent}</p>
                      </div>
                    )}
                    {framePreviewUrls.length > 0 && (
                      <div className="-mx-1 flex gap-1 overflow-x-auto px-1 pt-1">
                        {framePreviewUrls.map((url, index) => (
                          <img key={url} src={url} alt={`Bildruta ${index + 1}`} className="h-12 w-12 shrink-0 rounded-sm object-cover" />
                        ))}
                      </div>
                    )}
                  </div>

                </details>
              )}
            </div>
          </div>

        )}
      </div>

      {error && (
        <p className="mt-4 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-200">
          {error}
        </p>
      )}

      {savedRunLabel && step === "done" && (
        <p className="mt-4 rounded-xl border border-border bg-card px-4 py-3 text-sm">
          Sparat som <strong>{savedRunLabel}</strong> — prylarna ligger nu i listan nedanför.
        </p>
      )}

      {items && (
        <section className="mt-8">
          <div className="mb-3 flex items-center justify-between gap-3">
            <h2 className="flex items-center gap-2 font-heading text-xl">
              <ScanSearch className="h-5 w-5 text-primary" />
              {items.length} prylar hittade
            </h2>
            {total && items.length > 0 && (
              <p className="text-sm text-muted-foreground">
                Totalt <strong className="text-foreground">{kr(total.low)}</strong>–
                <strong className="text-foreground">{kr(total.high)}</strong>
              </p>
            )}
          </div>

          {items.length === 0 ? (
            <p className="rounded-xl border border-border bg-card px-4 py-6 text-center text-muted-foreground">
              Vi hittade inga säljbara prylar i filmen. Prova att filma närmare och långsammare.
            </p>
          ) : (
            <ul className="divide-y divide-border overflow-hidden rounded-xl border border-border bg-card">
              {items.map((item, i) => (
                <li key={`${item.name}-${i}`} className="flex items-start gap-4 p-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium">
                        {item.name}
                        {item.quantity && item.quantity > 1 ? ` ×${item.quantity}` : ""}
                      </span>
                      <span
                        className={cn(
                          "rounded-full px-2 py-0.5 text-[11px] font-semibold leading-none",
                          CONFIDENCE[item.confidence].className,
                        )}
                      >
                        {CONFIDENCE[item.confidence].label}
                      </span>
                    </div>
                    {item.descriptor && (
                      <p className="mt-1 text-sm text-muted-foreground">{item.descriptor}</p>
                    )}
                    {item.note && <p className="mt-1 text-sm text-muted-foreground">{item.note}</p>}
                    {typeof item.atSecond === "number" && (
                      <p className="mt-1 text-xs text-muted-foreground">
                        Syns vid {item.atSecond.toFixed(1).replace(".", ",")} sek
                      </p>
                    )}
                    {item.needsCloserLook && (
                      <p className="mt-1 text-xs font-medium text-amber-700 dark:text-amber-300">
                        Filma eller fota närmare för säkrare värdering.
                      </p>
                    )}
                  </div>
                  <div className="shrink-0 text-right">
                    <p className="font-heading text-lg leading-tight">
                      {item.valueLow === item.valueHigh
                        ? kr(item.valueHigh)
                        : `${item.valueLow.toLocaleString("sv-SE")}–${kr(item.valueHigh)}`}
                    </p>
                    <p className="text-xs text-muted-foreground">uppskattat värde</p>
                  </div>
                </li>
              ))}
            </ul>
          )}

          <p className="mt-4 text-xs text-muted-foreground">
            {savedRunLabel ? `Sparat som ”${savedRunLabel}”. ` : ""}Värdena är uppskattningar av vad prylarna kan säljas för begagnat i Sverige — inte
            en officiell värdering. Vill du sälja något fotar du prylen separat, så blir bilden
            säljbar. Filmen raderas automatiskt efter skanningen.
          </p>
        </section>
      )}
    </div>
  );
}
